/**
 * When the viewer's own search finds nothing, look up the words that are
 * already on the page (including ink the scanner did not draw) and hand
 * those positions back so the find box highlights them.
 */

import { searchDocumentTextLayer } from "../service/analysis";

const FOUND = 2;

export function toViewerSearchResults(hits) {
  return (hits || [])
    .filter((hit) => hit && hit.page && Array.isArray(hit.quads) && hit.quads.length)
    .map((hit) => {
      const text = String(hit.text || "");
      return {
        resultCode: FOUND,
        resultStr: text,
        resultStrStart: 0,
        resultStrEnd: text.length,
        pageNum: hit.page,
        ambientStr: text,
        quads: hit.quads,
      };
    });
}

async function defaultFetch(documentId, query, options) {
  const response = await searchDocumentTextLayer(documentId, query, options);
  return response?.data?.results || [];
}

function paint(instance, hits) {
  const results = toViewerSearchResults(hits);
  const documentViewer = instance?.Core?.documentViewer;
  if (!results.length || !documentViewer) return false;
  if (typeof documentViewer.displayAdditionalSearchResults === "function") {
    documentViewer.displayAdditionalSearchResults(results);
  }
  if (typeof documentViewer.displaySearchResult === "function") {
    documentViewer.displaySearchResult(results[0]);
  }
  return true;
}

/**
 * Paint highlights for `query`. A newer search on the same viewer cancels
 * an older one that is still waiting. `fetchHits` is injectable for tests.
 */
export async function highlightHiddenText(
  instance,
  documentId,
  query,
  options = {},
  fetchHits = defaultFetch,
) {
  if (!instance) return false;
  const token = (instance.__hiddenTextToken || 0) + 1;
  instance.__hiddenTextToken = token;
  const hits = await fetchHits(documentId, query, options);
  if (!instance || instance.__hiddenTextToken !== token) return false;
  instance.__hiddenTextApplying = true;
  try {
    return paint(instance, hits);
  } finally {
    instance.__hiddenTextApplying = false;
  }
}

/**
 * Listen to the find box on this viewer. Safe to call again when the
 * open document changes: the listener is attached once, the id updates.
 */
export function ensureViewerWordSearch(instance, documentId) {
  if (!instance?.UI || typeof instance.UI.addSearchListener !== "function") return;
  instance.__viewerWordSearchDocumentId = documentId;
  if (instance.__viewerWordSearchAttached) return;
  instance.__viewerWordSearchAttached = true;
  instance.UI.addSearchListener((searchValue, options, results) => {
    if (instance.__hiddenTextApplying) return;
    const query = String(searchValue || "").trim();
    if (!query || (results && results.length > 0)) return;
    const id = instance.__viewerWordSearchDocumentId;
    if (!id) return;
    const now = Date.now();
    if (
      instance.__hiddenTextLastQuery === query
      && now - (instance.__hiddenTextLastAt || 0) < 1500
    ) {
      return;
    }
    instance.__hiddenTextLastQuery = query;
    instance.__hiddenTextLastAt = now;
    const wholeWord = !!(options && options.wholeWord);
    highlightHiddenText(instance, id, query, { wholeWord }).catch(() => {});
  });
}
